import { useModal } from '@ebay/nice-modal-react';
import Divider from '@shared/components/Divider/Divider';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Messages } from 'assets/icons/Messages.svg';
import { ReactComponent as Refresh } from 'assets/icons/Refresh.svg';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useMatrixDeviceVerificationContext from 'contexts/MatrixDeviceVerificationContext/useMatrixDeviceVerificationContext';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import AccountSettingsModal from 'modules/Authentication/Settings/AccountSettings';
import ResetKeysModal from 'modules/EndToEndEncryption/ResetKeysModal/ResetKeysModal';
import ChangeSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/ChangeSecurityCodeModal/ChangeSecurityCodeModal';
import CreateSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/CreateSecurityCodeModal/CreateSecurityCodeModal';
import UnlockWithSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/UnlockWithSecurityCodeModal/UnlockWithSecurityCodeModal';
import SettingsCard from 'modules/SettingsModal/SettingsCard/SettingsCard';

import SecuritySettingsOptionRow from './SecuritySettingsOptionRow';
import SecuritySettingsWarningBox from './SecuritySettingsWarningBox';

const SETTINGS_CARD_INNER_CLASS = 'h-full w-full p-4 lg:p-2';
const SETTINGS_CARD_HEADER_CLASS = 'px-0 lg:px-2';
const SETTINGS_CARD_CONTENT_CLASS = 'flex flex-col gap-4 px-0 py-4 lg:px-2';

const SecuritySettingsTab = (): JSX.Element | null => {
  const { t } = useTranslation();
  const {
    crossSigningStatus,
    devicesVerificationStatus,
    refreshCurrentDeviceStatus,
    markDeviceVerified,
    checkForVerifiedDevices,
    hasSecurityCode,
    refreshHasSecurityCode,
    isResettingKeys,
  } = useMatrixDeviceContext();
  const { setHasChosenSasVerification, beginWaitingDeviceFlow } = useMatrixDeviceVerificationContext();
  const { mxLoggedIn } = useMxCookies();
  const { remove: closeAccountSettingsModal } = useModal(AccountSettingsModal);
  const changeSecurityCodeModal = useModal(ChangeSecurityCodeModal);
  const createSecurityCodeModal = useModal(CreateSecurityCodeModal);
  const unlockWithSecurityCodeModal = useModal(UnlockWithSecurityCodeModal);
  const { show: showResetKeysModal } = useModal(ResetKeysModal);

  const isVerified = crossSigningStatus === CrossSigningStatusEnum.VERIFIED;
  const isUnverified = crossSigningStatus === CrossSigningStatusEnum.NOT_VERIFIED;
  const isUnverifiedHasDevices = isUnverified && devicesVerificationStatus === CrossSigningStatusEnum.VERIFIED;
  const isUnverifiedHasSecurityCode = isUnverified && hasSecurityCode === true;
  const isVerifiedNoCode = isVerified && hasSecurityCode === false;
  const shouldShowVerifiedSecurityCodeRow = isVerified && hasSecurityCode === true;
  const shouldShowEndToEndEncryptionCard =
    isUnverifiedHasDevices || isUnverifiedHasSecurityCode || shouldShowVerifiedSecurityCodeRow || isVerifiedNoCode;
  const shouldShowResetCard = isUnverified;

  useEffect(() => {
    if (!mxLoggedIn) return;

    refreshCurrentDeviceStatus();
    checkForVerifiedDevices();
    refreshHasSecurityCode();
  }, [mxLoggedIn, refreshCurrentDeviceStatus, checkForVerifiedDevices, refreshHasSecurityCode]);

  const handlePinSuccess = () => {
    markDeviceVerified();
    refreshHasSecurityCode();
    checkForVerifiedDevices();
  };

  const handleChangeSecurityCode = () => {
    changeSecurityCodeModal.show({ onSuccess: handlePinSuccess });
  };

  const handleSetUpSecurityCode = () => {
    createSecurityCodeModal.show({ mode: 'create', showSetupDescription: true, onSuccess: handlePinSuccess });
  };

  const handleVerifyFromDevice = async () => {
    setHasChosenSasVerification(true);
    closeAccountSettingsModal();
    await beginWaitingDeviceFlow();
  };

  const handleVerifyWithSecurityCode = () => {
    unlockWithSecurityCodeModal.show({ onSuccess: handlePinSuccess });
  };

  const handleResetAccount = () => {
    showResetKeysModal();
  };

  if (!mxLoggedIn) return null;

  return (
    <div className="flex flex-col space-y-4">
      <span className="text-xl font-semibold leading-[1.25] text-grayscale-black">{t('security')}</span>
      {shouldShowEndToEndEncryptionCard && (
        <SettingsCard>
          <div className={SETTINGS_CARD_INNER_CLASS}>
            <SettingsCard.Header
              icon={Messages}
              title={t('verification.end_to_end_encrypted_chats')}
              className={SETTINGS_CARD_HEADER_CLASS}
              iconClassName="text-gray-70"
            />
            {isUnverifiedHasDevices && (
              <SettingsCard.Content className={SETTINGS_CARD_CONTENT_CLASS}>
                <SecuritySettingsOptionRow
                  title={t('verification.verify_from_another_device')}
                  description={t('verification.device_needs_verification_description')}
                  buttonLabel={t('verification.verify')}
                  buttonVariant="primary"
                  onClick={handleVerifyFromDevice}
                />
              </SettingsCard.Content>
            )}
            {isUnverifiedHasDevices && isUnverifiedHasSecurityCode && <Divider />}
            {isUnverifiedHasSecurityCode && (
              <SettingsCard.Content className={SETTINGS_CARD_CONTENT_CLASS}>
                <SecuritySettingsOptionRow
                  title={t('verification.verify_with_security_code')}
                  description={t('verification.enter_security_code_to_unlock_description')}
                  buttonLabel={t('verification.verify')}
                  buttonVariant="primary"
                  onClick={handleVerifyWithSecurityCode}
                />
              </SettingsCard.Content>
            )}
            {shouldShowVerifiedSecurityCodeRow && (
              <SettingsCard.Content className={SETTINGS_CARD_CONTENT_CLASS}>
                <SecuritySettingsOptionRow
                  title={t('verification.security_code')}
                  description={t('verification.security_code_settings_description')}
                  buttonLabel={t('verification.change')}
                  buttonVariant="secondary"
                  onClick={handleChangeSecurityCode}
                />
              </SettingsCard.Content>
            )}
            {isVerifiedNoCode && (
              <SettingsCard.Content className={SETTINGS_CARD_CONTENT_CLASS}>
                <SecuritySettingsOptionRow
                  title={t('verification.security_code')}
                  description={t('verification.security_code_settings_description')}
                  buttonLabel={t('verification.set_up')}
                  buttonVariant="primary"
                  onClick={handleSetUpSecurityCode}
                />
              </SettingsCard.Content>
            )}
          </div>
        </SettingsCard>
      )}
      {shouldShowResetCard && (
        <SettingsCard>
          <div className={SETTINGS_CARD_INNER_CLASS}>
            <SettingsCard.Header
              icon={Refresh}
              title={t('verification.reset')}
              className={SETTINGS_CARD_HEADER_CLASS}
              iconClassName="text-gray-70"
            />
            <SettingsCard.Content className={SETTINGS_CARD_CONTENT_CLASS}>
              <SecuritySettingsOptionRow
                title={t('verification.reset_account')}
                description={t('verification.reset_account_description')}
                buttonLabel={t('verification.reset_account')}
                buttonVariant="secondaryTertiaryOnGray"
                onClick={handleResetAccount}
                disabled={isResettingKeys}
                loading={isResettingKeys}
                loadingText={t('verification.resetting')}
                buttonClassName="!bg-white !text-tertiary-60 hover:!bg-white hover:!text-tertiary-70"
              />
              <SecuritySettingsWarningBox>{t('verification.reset_account_warning')}</SecuritySettingsWarningBox>
            </SettingsCard.Content>
          </div>
        </SettingsCard>
      )}
    </div>
  );
};

export default SecuritySettingsTab;
